import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const OneRupeeEstimate = () => {
  const signupUrl = `${process.env.REACT_APP_SIGNUP_URL}?plan=9&code=PRIME-YEAR-2999`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "myBOQ Civil Estimation Software",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://myboq.in/1-rupee-estimate",
    description:
      "myBOQ helps civil engineers, contractors and consultants create BOQ, PWD SSR estimates, rate analysis, abstracts and recap reports. Prime Yearly Plan offers estimates at ₹1 per estimate.",
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      price: "1",
      availability: "https://schema.org/InStock",
      description: "₹1 per estimate applicable with Prime Yearly Plan",
      url: "https://myboq.in/1-rupee-estimate",
    },
    publisher: {
      "@type": "Organization",
      name: "SiliconMount Tech Services Pvt. Ltd.",
      url: "https://siliconmount.com",
    },
  };

  const features = [
    "PWD SSR-compliant estimate generation",
    "BOQ, Abstract, Recap and Rate Analysis reports",
    "Professional PDF and Excel-ready outputs",
    "Built for engineers, contractors and consultants",
    "Upgrade path to Unlimited Lifetime Plan",
    "Secure web-based access with guided workflow",
  ];

  const faqs = [
    {
      q: "Is the ₹1 per estimate offer available for everyone?",
      a: "The ₹1 per estimate pricing is applicable when you purchase the myBOQ Prime Yearly Plan. It is designed for users who create regular civil estimates and want the lowest effective estimate cost.",
    },
    {
      q: "What can I create using myBOQ?",
      a: "You can create BOQ, PWD SSR estimates, rate analysis, abstracts, recap sheets and professional civil estimation reports through a structured workflow.",
    },
    {
      q: "Who should use myBOQ?",
      a: "myBOQ is useful for civil engineers, contractors, consultants, builders, infrastructure professionals and teams working with standardized estimation workflows.",
    },
    {
      q: "Can I upgrade later?",
      a: "Yes. You can start with Prime Yearly and later upgrade to the Unlimited Lifetime Plan if your estimate volume grows.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>₹1 Per Estimate | Civil BOQ & PWD SSR Estimation Software | myBOQ</title>
        <meta
          name="description"
          content="Create BOQ, PWD SSR estimates, rate analysis, abstracts and recap reports at just ₹1 per estimate with myBOQ Prime Yearly Plan. Built for civil engineers and contractors in India."
        />
        <meta
          name="keywords"
          content="₹1 per estimate, 1 rupee estimate, BOQ software India, PWD SSR estimate software, civil estimation software, rate analysis software, construction estimate software, myBOQ pricing"
        />
        <link rel="canonical" href="https://myboq.in/1-rupee-estimate" />

        <meta property="og:title" content="Create Civil Estimates at ₹1 Per Estimate | myBOQ" />
        <meta
          property="og:description"
          content="Start creating professional BOQ, PWD SSR estimates, abstracts, rate analysis and recap reports at just ₹1 per estimate with myBOQ Prime Yearly Plan."
        />
        <meta property="og:url" content="https://myboq.in/1-rupee-estimate" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://myboq.in/PrimeYearly_Unlimited_Slider_V3.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="₹1 Per Estimate Civil Estimation Software | myBOQ" />
        <meta
          name="twitter:description"
          content="BOQ, PWD SSR estimates, abstracts, rate analysis and recap reports at ₹1 per estimate with myBOQ Prime Yearly."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <main className="min-h-screen bg-slate-50">
        <section className="relative overflow-hidden bg-slate-950 px-4 py-16 text-white sm:py-20 lg:py-24">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute left-10 top-10 h-72 w-72 rounded-full bg-orange-500 blur-3xl" />
            <div className="absolute bottom-0 right-10 h-80 w-80 rounded-full bg-blue-600 blur-3xl" />
          </div>

          <div className="relative mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex rounded-full border border-orange-300/30 bg-orange-400/10 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-orange-200">
                Limited Pricing Offer
              </span>
              <h1 className="mt-6 text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
                Create Professional BOQ & PWD Estimates at Just ₹1 per Estimate
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
                myBOQ helps civil engineers, contractors and consultants generate SSR-compliant estimates, abstracts, recap sheets and rate analysis reports faster with a simple Prime Yearly Plan.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={signupUrl}
                  className="rounded-2xl bg-orange-600 px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-500/30 transition hover:bg-orange-700"
                >
                  Start for ₹1 per Estimate
                </a>
                <Link
                  to="/pricing"
                  className="rounded-2xl border border-white/20 bg-white/10 px-6 py-3 text-sm font-extrabold text-white backdrop-blur transition hover:bg-white/15"
                >
                  View All Plans
                </Link>
              </div>

              <p className="mt-4 text-xs text-slate-400">
                ₹1 per estimate is applicable with the Prime Yearly Plan purchase.
              </p>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/10 p-4 shadow-2xl backdrop-blur">
              <img
                src="/PrimeYearly_Unlimited_Slider_V3.png"
                alt="myBOQ ₹1 per estimate and unlimited lifetime pricing"
                className="w-full rounded-3xl object-cover shadow-xl"
              />
            </div>
          </div>
        </section>

        <section className="px-4 py-14 lg:py-18">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60">
                <p className="text-3xl font-black text-orange-600">₹1</p>
                <h2 className="mt-2 text-xl font-black text-slate-950">Per Estimate</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">Lowest effective estimate cost with Prime Yearly Plan.</p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60">
                <p className="text-3xl font-black text-slate-950">SSR</p>
                <h2 className="mt-2 text-xl font-black text-slate-950">PWD Estimate Workflow</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">Generate standardized civil estimation outputs in minutes.</p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60">
                <p className="text-3xl font-black text-blue-700">∞</p>
                <h2 className="mt-2 text-xl font-black text-slate-950">Scale to Unlimited</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">Upgrade later to Unlimited Lifetime when project volume grows.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white px-4 py-14">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">Why this offer matters</p>
                <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl">
                  Affordable civil estimation software for Indian engineers and contractors
                </h2>
                <p className="mt-5 text-base leading-8 text-slate-600">
                  Manual estimation takes time, creates version-control issues and makes report generation repetitive. myBOQ standardizes estimation workflows so users can create professional BOQ, PWD SSR estimates, abstracts, recap sheets and rate analysis reports with less effort.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {features.map((feature) => (
                  <div key={feature} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">✓</div>
                    <p className="text-sm font-bold leading-6 text-slate-800">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-14">
          <div className="mx-auto max-w-5xl text-center">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">SEO-friendly use case</p>
            <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl">
              What is included in the ₹1 per estimate model?
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-slate-600">
              The Prime Yearly Plan is designed for regular estimation work. It helps users reduce cost per estimate while still producing professional civil estimation reports required for project planning, tender preparation, contractor work and infrastructure documentation.
            </p>
          </div>
        </section>

        <section className="bg-slate-950 px-4 py-14 text-white">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 className="text-3xl font-black sm:text-4xl">Start at ₹1. Scale to Unlimited.</h2>
                <p className="mt-4 text-base leading-8 text-slate-300">
                  Start with the Prime Yearly Plan and upgrade to Unlimited Lifetime when you need unlimited estimates, unlimited project usage and premium estimation features.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <a href={signupUrl} className="rounded-2xl bg-orange-600 px-6 py-3 text-sm font-extrabold text-white hover:bg-orange-700">
                  Start for ₹1
                </a>
                <Link to="/pricing" className="rounded-2xl bg-amber-300 px-6 py-3 text-sm font-extrabold text-slate-950 hover:bg-amber-200">
                  Go Unlimited
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-14">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-center text-3xl font-black text-slate-950">Frequently Asked Questions</h2>
            <div className="mt-8 space-y-4">
              {faqs.map((faq) => (
                <details key={faq.q} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <summary className="cursor-pointer text-base font-extrabold text-slate-950">{faq.q}</summary>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default OneRupeeEstimate;
